import React, { useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { joApi, joErr } from '@/services/processingJoApi';
import { fmtN } from './JoBits';

const CloseDialog = ({ o, open, onOpenChange, onDone }) => {
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const short = o.journey ? o.journey.totals.in_progress_pcs : Math.max(0, o.transferred_pcs - o.received_pcs);
  const unsent = o.balance_to_send;
  const shortage = short > 0 || unsent > 0;
  const live = (o.slips || []).filter((s) => ['OPEN', 'PICKING', 'PICKED'].includes(s.status));
  const run = async () => {
    setBusy(true);
    try { await joApi.close(o.jo_id, { confirm_shortage: shortage, remarks }); toast.success(`${o.jo_id} closed`); onOpenChange(false); onDone(); }
    catch (e) { toast.error(joErr(e, 'Could not close')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="jo-close-dialog">
        <DialogHeader><DialogTitle>Close {o.jo_id}</DialogTitle>
          <DialogDescription>{shortage ? 'This closes the order early — the shortage below is recorded.' : 'Every piece sent has reached SFG Storage or was counted as lost.'}</DialogDescription></DialogHeader>
        {live.length > 0 && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="jo-close-live">Pick slip {live.map((s) => s.slip_id).join(', ')} is still open in Greige Storage — it must be transferred or cancelled first.</p>}
        {shortage && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-1" data-testid="jo-close-shortage">
            <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4" />Shortage to confirm</p>
            <p>Not yet in SFG Storage: <b data-testid="jo-close-short-pcs">{fmtN(short)}</b> pcs</p>
            <p>Never sent (balance): <b data-testid="jo-close-unsent-pcs">{fmtN(unsent)}</b> pcs</p>
          </div>
        )}
        <div className="space-y-1.5"><Label>{shortage ? 'Reason for the shortage *' : 'Remarks'}</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="jo-close-remarks" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Back</Button>
          <Button onClick={run} disabled={busy || live.length > 0 || (shortage && !remarks.trim())} className={shortage ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#2D5F4F] hover:bg-[#234A3D]'} data-testid="jo-close-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{shortage ? 'Confirm shortage & close' : 'Close job order'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CancelBalanceDialog = ({ o, open, onOpenChange, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await joApi.cancelBalance(o.jo_id, reason); toast.success(`${fmtN(r.cancelled_pcs)} pcs cancelled on ${o.jo_id}`); onOpenChange(false); onDone(); }
    catch (e) { toast.error(joErr(e, 'Could not cancel the balance')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="jo-cancel-balance-dialog">
        <DialogHeader><DialogTitle>Cancel the balance</DialogTitle>
          <DialogDescription>{fmtN(o.balance_to_send)} pcs that are not yet on a pick slip will not be sent. Greige Storage stops seeing them.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="jo-cancel-balance-reason" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Back</Button>
          <Button onClick={run} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="jo-cancel-balance-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Cancel {fmtN(o.balance_to_send)} pcs</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Processing actions on an open job order: cancel the unsent balance, close (with shortage confirmation). */
export const JoActions = ({ o, onDone }) => {
  const [dlg, setDlg] = useState('');
  if (o.status !== 'OPEN') return null;
  return (
    <div className="flex flex-wrap gap-2">
      {o.balance_to_send > 0 && <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setDlg('cancel')} data-testid="jo-cancel-balance-btn"><Ban className="w-4 h-4 mr-1" />Cancel balance</Button>}
      <Button onClick={() => setDlg('close')} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="jo-close-btn"><CheckCircle2 className="w-4 h-4 mr-1" />Close job order</Button>
      <CloseDialog o={o} open={dlg === 'close'} onOpenChange={(v) => setDlg(v ? 'close' : '')} onDone={onDone} />
      <CancelBalanceDialog o={o} open={dlg === 'cancel'} onOpenChange={(v) => setDlg(v ? 'cancel' : '')} onDone={onDone} />
    </div>
  );
};
